import { useSyncExternalStore } from 'react';
import { debounce } from '@/modules/debounce';

let width = typeof window !== 'undefined' ? window.innerWidth : 0;
const subscribers = new Set<() => void>();

if (typeof window !== 'undefined') {
  window.addEventListener(
    'resize',
    debounce(() => {
      width = window.innerWidth;
      subscribers.forEach((cb) => cb());
    }, 300),
  );
}

const subscribe = (cb: () => void) => {
  subscribers.add(cb);
  // Stryker disable next-line BlockStatement: leak-hygiene only — a stale subscriber is a no-op for an unmounted consumer (the module-scope Set is private), so the removal is unobservable through the hook API
  return () => {
    // Stryker disable next-line CallExpression: leak-hygiene only — same equivalence as the BlockStatement above: a stale subscriber is a no-op for an unmounted consumer
    subscribers.delete(cb);
  };
};

const getSnapshot = () => width;
const getServerSnapshot = () => 0;

// The viewport width, for JS-side responsive branching (breakpoint checks
// that CSS media queries can't express). One module-scope store shared by
// every importer in the JS realm: a single window resize listener feeds a
// single snapshot, so all consumers re-render together from the same value.
// Updates are DEBOUNCED — the value settles 300ms after the last resize
// event and rapid resize bursts coalesce into one update (consumers see the
// settled width, never mid-drag thrash). The initial value is
// window.innerWidth read at module load; in window-less environments the
// hook returns 0.
export function useWindowWidth(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

// The JS twin of Tailwind's default `md` breakpoint (768px): ≤767 is the
// phone layout wherever a CSS media query cannot express the branch. One
// spelling for every consumer — the two spellings (`<= 767` vs `< 768`)
// diverged for fractional widths under zoom.
const MOBILE_MAX_WIDTH = 767;

export function useIsMobile(): boolean {
  return useWindowWidth() <= MOBILE_MAX_WIDTH;
}
